import { useEffect, useState, useRef, useCallback } from "react";
import { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { AUTH_BASE_URL, LOGIN_URL } from "@/config/auth";
export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      userIdRef.current = currentSession?.user?.id ?? null;
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, newSession: Session | null) => {
        if (event === 'TOKEN_REFRESHED') {
          return;
        }
        
        const newUserId = newSession?.user?.id ?? null;
        
        if (newUserId !== userIdRef.current || event === 'SIGNED_OUT') {
          setSession(newSession);
          setUser(newSession?.user ?? null);
          userIdRef.current = newUserId;
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    const isSSOSession = sessionStorage.getItem('sso_authenticated') === 'true';
    const ssoConfigId = sessionStorage.getItem('sso_config_id');
    
    userIdRef.current = null;
    
    sessionStorage.removeItem('sso_authenticated');
    sessionStorage.removeItem('sso_config_id');
    
    if (isSSOSession && ssoConfigId) {
      console.log('[useAuth] OIDC SSO session detected, initiating RP-Initiated Logout');
      
      await supabase.auth.signOut();
      
      const logoutUrl = new URL(`${AUTH_BASE_URL}/oidc-logout`);
      logoutUrl.searchParams.set('config_id', ssoConfigId);
      logoutUrl.searchParams.set('post_logout_redirect', LOGIN_URL);
      
      window.location.href = logoutUrl.toString();
      return;
    }
    
    await supabase.auth.signOut();
    window.location.href = LOGIN_URL;
  }, []);

  return { user, session, loading, signOut };
};
